import { Button, Form, message, Modal, Space } from 'antd';
import type { Moment } from 'moment';
import React, { useState } from 'react';

import { nonCleanItemKeys } from '../../constants';
import type {
  AddReservationFormValue,
  CleanReserveList,
} from '../../sharedTypes';
import { fetchReservationDatesStatus, getTicketId } from '../helper';
import OrderContent from '../OrderContent';
import type { UpdateReservationModalProps } from './types';

const { useForm } = Form;

const UpdateReservationModal = ({
  isEditReservation,
  editingReservationData,
  cleanReserveList,
  quantityError,
  mainForm,
  userTicketAmount,
  reservationDatesStatusState,
  cleanKindInformation,
}: UpdateReservationModalProps) => {
  const { setState: updateIsEditReservation } = isEditReservation;
  const { data: editingReservation, reset: clearEditingReservation } =
    editingReservationData;
  const { data: cleanReserveListData, setState: updateCleanReserveList } =
    cleanReserveList;
  const { isShowQuantityError, setState: updateIsShowQuantityError } =
    quantityError;

  const [form] = useForm<{
    [key: string]: number | Moment | undefined;
    date: Moment;
    freeItem?: number;
  }>();

  const [isOpenModal, setIsOpenModal] = useState(false);

  const checkIsPaidItemsValidated = (
    values: AddReservationFormValue & { freeItem: number } & {
      [itemId: string]: number | null;
    }
  ) => {
    const defaultFormFieldNames = [
      'phoneNumber',
      'cleanKind',
      'communityAndHouseholdNumber',
    ];

    const isOnlyDefaultFormFieldNames = Object.keys(values).every((key) =>
      defaultFormFieldNames.includes(key)
    );
    if (isOnlyDefaultFormFieldNames) {
      return true;
    }

    const isPaidItemsValidated = Object.keys(values).some((key) => {
      if (nonCleanItemKeys.includes(key)) {
        return false;
      }
      if (!values[key]) {
        return false;
      }
      return true;
    });
    return isPaidItemsValidated;
  };

  const addReservation = async (
    formValues: AddReservationFormValue & { freeItem: number } & {
      [itemId: string]: number | null;
    }
  ) => {
    const {
      phoneNumber: { value: userId },
      cleanKind,
      date,
      communityAndHouseholdNumber,
    } = formValues;

    const communityName = communityAndHouseholdNumber.split('/')[0];
    const householdNumber = communityAndHouseholdNumber.split('/')[1];

    let outputObject: CleanReserveList = {
      userId,
      cleanKindId: cleanKind,
      communityName,
      householdNumber,
      clean: [
        {
          items: [],
          date,
        },
      ],
    };

    if (cleanReserveListData && cleanReserveListData?.clean.length !== 0) {
      outputObject = { ...cleanReserveListData };
    }

    for (const key in formValues) {
      const isNotCleanItem = nonCleanItemKeys.includes(key);
      const isFreeItem = key.includes('freeItem');
      if (
        isNotCleanItem === false &&
        isFreeItem === false &&
        Object.prototype.hasOwnProperty.call(formValues, key)
      ) {
        let quantity = formValues[key];
        if (formValues[key] === null) {
          quantity = null;
        }
        outputObject.clean[outputObject.clean.length - 1].items.push({
          id: key,
          quantity,
          isFreeItem: false,
        });
      }

      if (isFreeItem && Object.prototype.hasOwnProperty.call(formValues, key)) {
        const freeItemId = key.split('/')[1];
        outputObject.clean[outputObject.clean.length - 1].items.push({
          id: freeItemId,
          quantity: 1,
          isFreeItem: true,
        });
      }
    }

    const ticketId = getTicketId(cleanKindInformation?.cleanServiceItems);

    if (outputObject && ticketId) {
      const selectedDates = outputObject.clean.map((reservation) =>
        reservation.date.valueOf()
      );
      const newReservationDatesStatus = await fetchReservationDatesStatus({
        cleanKindInformation,
        cleanReserveList: outputObject,
        userId,
        selectedDates,
      });

      reservationDatesStatusState.setState(newReservationDatesStatus);
    }

    if (userId) {
      updateCleanReserveList(outputObject);
    }
    if (isShowQuantityError) {
      updateIsShowQuantityError(false);
    }
    if (
      userId &&
      cleanReserveListData &&
      cleanReserveListData.clean.length !== 0
    ) {
      setIsOpenModal(true);
    }
  };

  const closeModal = () => {
    if (editingReservation) {
      updateIsEditReservation(false);
      clearEditingReservation();
    }
    setIsOpenModal(false);
  };

  const updateReservations = async (formValues: {
    [key: string]: number | Moment | undefined;
    date: Moment;
    freeItem?: number;
  }) => {
    const { date, freeItem, ...paidItems } = formValues;
    const isOnlyPaidItems = cleanKindInformation?.cleanServiceItems?.every(
      (item) => item.priceInfo.kind !== 'Free'
    );
    const isAllFailed = Object.keys(paidItems).every((key) => !paidItems[key]);
    if (isOnlyPaidItems && isAllFailed) {
      updateIsShowQuantityError(true);
      return;
    }

    const newReservation: CleanReserveList['clean'][0] = {
      items: [],
      date,
    };

    if (freeItem) {
      newReservation.items.push({
        id: 'freeItem',
        quantity: freeItem,
        isFreeItem: true,
      });
    }

    const newPaidItems = Object.keys(paidItems).map((key) => {
      const newItem = {
        id: key,
        quantity: paidItems[key] as number,
        isFreeItem: false,
      };
      return newItem;
    });

    const newItemList = newReservation.items.concat(newPaidItems);

    if (!cleanReserveListData) {
      return;
    }

    const newReservationList = { ...cleanReserveListData };
    if (editingReservation && editingReservation.order >= 0) {
      const { order } = editingReservation;
      const updatedReservation = {
        items: newItemList,
        date,
      };
      newReservationList.clean.splice(order, 1, updatedReservation);
    }

    if (!editingReservation) {
      newReservationList.clean.push({
        ...newReservation,
        items: newItemList,
      });
    }

    newReservationList.clean.sort((a, b) => {
      const dateA = a.date.valueOf();
      const dateB = b.date.valueOf();
      return dateA - dateB;
    });

    const selectedDates = newReservationList.clean.map((reservation) =>
      reservation.date.valueOf()
    );
    const ticketId = getTicketId(cleanKindInformation?.cleanServiceItems);
    if (ticketId) {
      const newReservationDatesStatus = await fetchReservationDatesStatus({
        cleanKindInformation,
        cleanReserveList: cleanReserveListData,
        userId: newReservationList.userId,
        selectedDates,
      });

      reservationDatesStatusState.setState(newReservationDatesStatus);
    }
    updateCleanReserveList({ ...newReservationList });
    closeModal();
  };

  const handleAddReservationButtonClick = async () => {
    const isOnlyPaidItems = cleanKindInformation?.cleanServiceItems?.every(
      (item) => item.priceInfo.kind !== 'Free'
    );
    const currentMainFormValues = mainForm.getFieldsValue();
    const isPaidItemsValidated = checkIsPaidItemsValidated(
      currentMainFormValues
    );
    try {
      const validatedMainFormValues = await mainForm.validateFields();
      if (isOnlyPaidItems && !isPaidItemsValidated) {
        updateIsShowQuantityError(true);
        return;
      }
      if (
        cleanReserveListData &&
        typeof userTicketAmount === 'number' &&
        cleanReserveListData.clean.length >= userTicketAmount
      ) {
        message.error('票券張數不足，無法繼續預約，請點擊前往付款', 5);
        return;
      }
      addReservation(validatedMainFormValues);
    } catch (error) {
      if (isOnlyPaidItems && !isPaidItemsValidated) {
        updateIsShowQuantityError(true);
      }
      return;
    }
  };

  return (
    <>
      <Button type="primary" onClick={handleAddReservationButtonClick}>
        {!cleanReserveListData || cleanReserveListData?.clean.length === 0
          ? '新增預約'
          : '繼續預約'}
      </Button>
      <Modal
        title={editingReservation ? '編輯預約' : '繼續預約'}
        centered
        destroyOnClose
        open={isOpenModal || isEditReservation.open}
        onCancel={closeModal}
        footer={
          <Space>
            <Button onClick={closeModal}>取消</Button>
            <Button type="primary" onClick={() => form.submit()}>
              確定
            </Button>
          </Space>
        }
      >
        <Form form={form} preserve={false} onFinish={updateReservations}>
          <OrderContent
            reservationList={cleanReserveListData}
            isShowQuantityError={isShowQuantityError}
            hideInputNumberErrorMsg={() => updateIsShowQuantityError(false)}
            editingReservation={editingReservation}
            reservationDatesStatusState={reservationDatesStatusState}
            cleanKindInformation={cleanKindInformation}
          />
        </Form>
      </Modal>
    </>
  );
};

export default UpdateReservationModal;
